import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import WhatsAppButton from '../../components/feature/WhatsAppButton';
import { blogArticles } from '../../mocks/blog';

const BlogPage = () => {
  const categories = ['Tous', 'Bien-être au travail', 'Maternité', 'Nutrition', 'Santé mentale', 'Innovation'];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <WhatsAppButton />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gradient-to-b from-primary/5 to-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-20">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-4xl mx-auto"
          >
            <h1 className="font-serif text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
              Blog & Conseils bien-être
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed">
              Découvrez nos articles et conseils pour améliorer votre santé et votre bien-être au quotidien
            </p>
          </motion.div>
        </div>
      </section>

      {/* Categories */}
      <section className="py-8 bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6 lg:px-20">
          <div className="flex flex-wrap justify-center gap-3">
            {categories.map((category, index) => (
              <motion.button
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.05 }}
                className={`px-6 py-2 rounded-full font-medium transition-all cursor-pointer whitespace-nowrap ${
                  index === 0
                    ? 'bg-primary text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {category}
              </motion.button>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Article */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-20">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="grid lg:grid-cols-2 gap-12 items-center bg-gradient-to-br from-gray-50 to-white rounded-3xl overflow-hidden shadow-xl"
          >
            <div className="h-full">
              <img
                src={blogArticles[0].image}
                alt={blogArticles[0].title}
                className="w-full h-full object-cover object-top min-h-[400px]"
              />
            </div>
            <div className="p-8 lg:p-12">
              <div className="inline-flex items-center space-x-2 bg-primary/10 px-4 py-2 rounded-full mb-4">
                <span className="text-sm font-medium text-primary">{blogArticles[0].category}</span>
              </div>
              <h2 className="font-serif text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
                {blogArticles[0].title}
              </h2>
              <p className="text-gray-600 leading-relaxed mb-6">
                {blogArticles[0].excerpt}
              </p>
              <div className="flex items-center space-x-6 text-sm text-gray-500 mb-6">
                <div className="flex items-center space-x-2">
                  <i className="ri-user-line"></i>
                  <span>{blogArticles[0].author}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <i className="ri-calendar-line"></i>
                  <span>{new Date(blogArticles[0].date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <i className="ri-time-line"></i>
                  <span>{blogArticles[0].readTime}</span>
                </div>
              </div>
              <Link
                to={`/blog/${blogArticles[0].slug}`}
                className="inline-flex items-center space-x-2 bg-primary hover:bg-primary-dark text-white px-6 py-3 rounded-full font-semibold transition-all cursor-pointer whitespace-nowrap"
              >
                <span>Lire l'article</span>
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Articles Grid */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-20">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {blogArticles.slice(1).map((article, index) => (
              <motion.article
                key={article.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all group cursor-pointer"
              >
                <div className="relative h-56 overflow-hidden">
                  <img
                    src={article.image}
                    alt={article.title}
                    className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute top-4 left-4">
                    <span className="bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full text-xs font-medium text-primary">
                      {article.category}
                    </span>
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="font-serif font-semibold text-xl text-gray-900 mb-3 group-hover:text-primary transition-colors line-clamp-2">
                    {article.title}
                  </h3>
                  <p className="text-gray-600 text-sm leading-relaxed mb-4 line-clamp-3">
                    {article.excerpt}
                  </p>
                  <div className="flex items-center justify-between text-xs text-gray-500 mb-4">
                    <span className="flex items-center space-x-1">
                      <i className="ri-user-line"></i>
                      <span>{article.author}</span>
                    </span>
                    <span className="flex items-center space-x-1">
                      <i className="ri-time-line"></i>
                      <span>{article.readTime}</span>
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {article.tags.slice(0, 3).map((tag, tagIndex) => (
                      <span
                        key={tagIndex}
                        className="bg-gray-100 text-gray-600 px-3 py-1 rounded-full text-xs"
                      >
                        #{tag}
                      </span>
                    ))}
                  </div>
                </div>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="py-20 bg-gradient-to-br from-primary to-primary-dark text-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-20 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="space-y-8"
          >
            <div className="w-16 h-16 flex items-center justify-center bg-white/20 rounded-full mx-auto">
              <i className="ri-mail-line text-3xl text-white"></i>
            </div>
            <h2 className="font-serif text-4xl lg:text-5xl font-bold">
              Restez informé de nos actualités
            </h2>
            <p className="text-xl text-white/90">
              Recevez nos derniers articles et conseils bien-être directement dans votre boîte mail
            </p>
            <div className="max-w-md mx-auto flex">
              <input
                type="email"
                placeholder="Votre adresse email"
                className="flex-1 px-6 py-4 rounded-l-full text-gray-900 focus:outline-none"
              />
              <button className="px-8 py-4 bg-accent hover:bg-accent-dark rounded-r-full font-semibold transition-all flex items-center justify-center cursor-pointer whitespace-nowrap">
                <span>S'abonner</span>
              </button>
            </div>
            <p className="text-white/70 text-sm">
              Pas de spam, uniquement du contenu de qualité. Désabonnement possible à tout moment.
            </p>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default BlogPage;